import type { ExtractedEvent } from '../types';
import { eventDayKey, formatDateHeader } from './eventDates';
import { formatTag } from './tagTaxonomy';

/** Field an event list can be grouped by. `'date'` groups by New York calendar
 *  day; the rest are simple string fields on the event. */
export type GroupByKey =
  | 'neighborhood'
  | 'category'
  | 'venue'
  | 'eventType'
  | 'series'
  | 'sourceType'
  | 'status'
  | 'date';

export interface GroupByOption {
  key: GroupByKey;
  label: string;
}

/** Order shown in the picker. */
export const GROUP_BY_OPTIONS: GroupByOption[] = [
  { key: 'neighborhood', label: 'Neighborhood' },
  { key: 'category', label: 'Category' },
  { key: 'venue', label: 'Venue' },
  { key: 'eventType', label: 'Event type' },
  { key: 'series', label: 'Series' },
  { key: 'sourceType', label: 'Source' },
  { key: 'status', label: 'Status' },
  { key: 'date', label: 'Date' },
];

const LABEL_BY_KEY: Record<GroupByKey, string> = Object.fromEntries(
  GROUP_BY_OPTIONS.map((o) => [o.key, o.label]),
) as Record<GroupByKey, string>;

export function groupByLabel(key: GroupByKey): string {
  return LABEL_BY_KEY[key];
}

/** Tree node produced by {@link groupEvents}. Leaf nodes have `events` populated
 *  and `children` empty; intermediate nodes have `children` populated and
 *  `events` empty. `value` is the raw bucket key (e.g. venueId, ISO date,
 *  neighborhood name, or `'__none__'` for missing values). `label` is the
 *  human-readable header. */
export interface EventGroupNode {
  /** Group-by key that produced this level (e.g. 'venue'). */
  key: GroupByKey;
  /** Bucket value within that key (raw, useful as React key). */
  value: string;
  /** Human-readable header. */
  label: string;
  /** Total event count in this subtree (recursively). */
  count: number;
  /** Direct child groups. Empty when this is a leaf. */
  children: EventGroupNode[];
  /** Events in this leaf bucket. Empty when `children.length > 0`. */
  events: ExtractedEvent[];
}

const NONE_VALUE = '__none__';

function bucketFor(event: ExtractedEvent, key: GroupByKey): { value: string; label: string } {
  switch (key) {
    case 'neighborhood': {
      const v = event.neighborhood?.trim();
      return v ? { value: v, label: v } : { value: NONE_VALUE, label: '(No neighborhood)' };
    }
    case 'category': {
      const v = event.category?.trim();
      return v ? { value: v, label: formatTag(v) } : { value: NONE_VALUE, label: '(No category)' };
    }
    case 'venue': {
      const id = event.venueId?.trim();
      const name = event.venueName?.trim() || event.locationText?.trim();
      if (id) return { value: id, label: name || '(Unnamed venue)' };
      return name
        ? { value: `name:${name.toLowerCase()}`, label: name }
        : { value: NONE_VALUE, label: '(No venue)' };
    }
    case 'eventType': {
      const v = event.eventType?.trim();
      return v ? { value: v, label: formatTag(v) } : { value: NONE_VALUE, label: '(No event type)' };
    }
    case 'series': {
      const id = event.seriesId?.trim();
      const name = event.seriesName?.trim();
      if (id) return { value: id, label: name || '(Unnamed series)' };
      return { value: NONE_VALUE, label: '(No series)' };
    }
    case 'sourceType':
      return { value: event.sourceType, label: formatTag(event.sourceType) };
    case 'status':
      return { value: event.status, label: formatTag(event.status) };
    case 'date': {
      const dayKey = eventDayKey(event);
      return dayKey
        ? { value: dayKey, label: formatDateHeader(dayKey) }
        : { value: NONE_VALUE, label: '(Invalid date)' };
    }
  }
}

/**
 * Recursively group `events` by the supplied `keys` (in order). Pass an empty
 * array to get back a single flat node containing every event. Buckets are
 * sorted alphabetically by label, with the `(No …)` bucket pinned last; date
 * buckets are sorted chronologically.
 */
export function groupEvents(
  events: ExtractedEvent[],
  keys: GroupByKey[],
): EventGroupNode[] {
  if (keys.length === 0) {
    return [
      {
        key: 'date',
        value: '__all__',
        label: 'All events',
        count: events.length,
        children: [],
        events,
      },
    ];
  }
  return groupRecursive(events, keys, 0);
}

function groupRecursive(
  events: ExtractedEvent[],
  keys: GroupByKey[],
  depth: number,
): EventGroupNode[] {
  const key = keys[depth];
  const buckets = new Map<string, { label: string; events: ExtractedEvent[] }>();
  for (const event of events) {
    const { value, label } = bucketFor(event, key);
    const existing = buckets.get(value);
    if (existing) existing.events.push(event);
    else buckets.set(value, { label, events: [event] });
  }
  const nodes: EventGroupNode[] = [];
  for (const [value, { label, events: bucketEvents }] of buckets) {
    const children =
      depth + 1 < keys.length
        ? groupRecursive(bucketEvents, keys, depth + 1)
        : [];
    nodes.push({
      key,
      value,
      label,
      count: bucketEvents.length,
      children,
      events: children.length > 0 ? [] : bucketEvents,
    });
  }
  sortGroupNodes(nodes, key);
  return nodes;
}

function sortGroupNodes(nodes: EventGroupNode[], key: GroupByKey): void {
  if (key === 'date') {
    nodes.sort((a, b) => {
      if (a.value === NONE_VALUE) return 1;
      if (b.value === NONE_VALUE) return -1;
      return a.value.localeCompare(b.value);
    });
    return;
  }
  nodes.sort((a, b) => {
    if (a.value === NONE_VALUE && b.value !== NONE_VALUE) return 1;
    if (b.value === NONE_VALUE && a.value !== NONE_VALUE) return -1;
    return a.label.localeCompare(b.label);
  });
}

/** Serialise a grouping list to a URL-safe `,` separated string. */
export function groupingToParam(keys: GroupByKey[]): string | undefined {
  return keys.length ? keys.join(',') : undefined;
}

const VALID_KEYS = new Set<GroupByKey>(GROUP_BY_OPTIONS.map((o) => o.key));

/** Parse the URL value back into a deduplicated, validated key list. */
export function groupingFromParam(value: string | null | undefined): GroupByKey[] {
  if (!value) return [];
  const seen = new Set<GroupByKey>();
  const out: GroupByKey[] = [];
  for (const raw of value.split(',')) {
    const trimmed = raw.trim() as GroupByKey;
    if (!VALID_KEYS.has(trimmed) || seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push(trimmed);
  }
  return out;
}
